<template>
    <div class="json-forms-section">
        <textarea v-if="editJSON" rows="16" style="width: 100%" :value="asString" @change="onStringChange" />
        <div v-else >
            <json-forms :data="valueOrDefault" :schema="options.json_schema" :uischema="options.json_edit_layout" :renderers="renderers" @change="onChange" />
        </div>
        <div class="text-right">
            <button class="btn btn-link btn-sm" @click.prevent="toggleEditJSON">
                {{ toggleLabel }}
            </button>
        </div>
    </div>
</template>

<script>
import { JsonForms } from "@jsonforms/vue2";
import { rankWith, uiTypeIs, and, schemaTypeIs, isControl } from '@jsonforms/core'
import {
  vanillaRenderers
} from "@jsonforms/vue2-vanilla"
import HelpRenderer from './jsonforms/HelpRenderer.vue';
import CheckboxRenderer from "./jsonforms/CheckboxRenderer.vue";

const customRenderers = [
    ...vanillaRenderers,
    {
        tester: rankWith(5, and(isControl, schemaTypeIs('boolean'))), // Ensures priority over default
        renderer: CheckboxRenderer
    },
    {
        tester: rankWith(1, uiTypeIs('Help')),
        renderer: HelpRenderer
    }
]

const jsonFormsClassOverrides = {
  control: {
    root: 'form-group',
    wrapper: '',
    label: 'form-label',
    description: 'control-description',
    input: 'form-control',
    error: 'form-control-error',
    textarea: 'form-control',
    select: 'form-control',
    option: 'form-control'
  },
  verticalLayout: {
    root: 'vertical-layout',
    item: 'vertical-layout-item'
  },
  horizontalLayout: {
    root: 'horizontal-layout',
    item: 'horizontal-layout-item'
  },
  group: {
    root: 'group',
    label: 'group-label',
    item: 'group-item'
  },
  arrayList: {
    root: 'array-list',
    legend: 'array-list-legend',
    addButton: 'btn btn-sm btn-primary',
    label: 'array-list-label',
    itemWrapper: 'array-list-item-wrapper',
    noData: 'array-list-no-data',
    item: 'array-list-item',
    itemToolbar: 'array-list-item-toolbar',
    itemLabel: 'array-list-item-label',
    itemContent: 'array-list-item-content',
    itemExpanded: 'expanded',
    itemMoveUp: 'btn btn-sm btn-primary toolbar-btn',
    itemMoveDown: 'btn btn-sm btn-primary toolbar-btn',
    itemDelete: 'btn btn-sm btn-danger toolbar-btn'
  },
  label: {
    root: 'label-element'
  }
};

export default {
    components: {
       JsonForms
    },
    provide() {
        return {
            styles: jsonFormsClassOverrides
        }
    },
    props: {
        // eslint-disable-next-line vue/require-prop-types
        value: {},
        options: {
            type: Object,
            require: true,
            default: () => ({})
        },
    },
    data() {
        return {
            editJSON: false
        }
    },
    computed: {
        renderers: function() {
            return customRenderers;
        },
        asString: function() {
            return this.value !== null
                ? JSON.stringify(this.value, null, 2)
                : '';
        },
        toggleLabel: function() {
            return this.editJSON ? "Return to form view" : "Edit as JSON";
        },
        valueOrDefault: function() {
            if (this.value !== null) {
                return this.value;
            }

            switch (this.options.json_schema.type) {
                case 'array':
                    return [];
                case 'number':
                    return 0;
                case 'string':
                    return '';
                case 'boolean':
                    return false;
                default:
                case 'object':
                    return {};
            }
        }
    },
    methods: {
        onChange: function(event) {
            this.$emit('input', event.data)
        },
        onStringChange: function(event) {
            if (event.target.value === ''){
                event.target.value = this.options.json_schema.type === 'array' ? '[]' : '{}'
            }
            this.$emit('input', JSON.parse(event.target.value))
        },
        toggleEditJSON: function() {
            this.editJSON = !this.editJSON
        }
    }
}
</script>

<style>
    .help-element {
        margin: .5em .25em;
    }
    .group-item {
        margin: 0 .5em;
    }
    .vertical-layout {
        display: flex;
        flex-direction: column;
        gap: .5em;
    }
    .horizontal-layout {
        display: flex;
        flex-direction: row;
        gap: .5em;
    }
    .horizontal-layout-item {
        flex-grow: 1;
    }

    .control-description {
        height: 1em;
    }
    .array-list-no-data {
        background-color: #eee;
        text-align: center;
        font-size: smaller;
        color:#999;
        padding: 1em;
        margin-bottom: 1em;
    }
    .array-list-item {
        margin: 0 1em;
        border-radius: .25em .25em 0 0;
        border: 1px solid #ccc;
        margin-bottom: 1em;
    }
    .array-list-item-toolbar {
        display: flex;
        border-radius: .25em .25em 0 0;
        background-color: #eee;
        border-bottom: 1px solid #ccc;
        padding: .25em;
        gap: .25em;
    }
    .toolbar-btn {
        width: 3em;
    }
    .form-label {
        font-size: 1em;
    }
    .group-label {
        font-size: 1em;
        font-weight: 700;
        margin-bottom: .5em;
    }
    .array-list-legend {
        font-size: 1em;
        display: flex;
        flex-direction: row-reverse;
        align-items: end;
        gap: .5em;
        padding: .25em;
    }
    .array-list-label {
        flex-grow: 1;
        margin-bottom: 0;
    }
    .array-list-item-label {
        flex-grow: 1;
        padding: .33em .5em;
    }
    .array-list-item-content {
        padding: .5em .5em 0 .5em;
    }

    .json-forms-section {
        padding: .5em;
        border: 1px solid #ccc;
    }
    .has-error .json-forms-section {
        border-color: #a94442;
    }
    .form-control-error {
        color: #a94442
    }
    .has-error .json-forms-section .form-control {
        border-color: #ccc;
    }
</style>
